import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'

const INPUT =
  'w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-white'
const LABEL = 'block text-sm font-medium text-gray-700 mb-1'

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount ?? 0)
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  })
}

const OTHER_CHURCH = '__other__'

const EMPTY_FORM = {
  full_name: '',
  date_of_birth: '',
  sex: '',
  phone: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  church: '',
  church_other: '',
  needs_transport: false,
}

export default function CampRegistration() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const campIdParam = searchParams.get('campId')

  const [openCamps, setOpenCamps] = useState([])
  const [camp, setCamp] = useState(null)
  const [tiers, setTiers] = useState([])
  const [cancelledRegId, setCancelledRegId] = useState(null)
  const [pageLoading, setPageLoading] = useState(true)
  const [step, setStep] = useState('form') // 'select' | 'form'
  const [selectLoading, setSelectLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [churches, setChurches] = useState([])
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => {
    supabase
      .from('churches')
      .select('id, name')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true })
      .then(({ data }) => setChurches(data ?? []))
  }, [])

  useEffect(() => {
    async function load() {
      if (campIdParam) {
        // A specific camp was linked from the Home page — skip the selector
        // Check if user already has an active registration for THIS camp
        const { data: existing } = await supabase
          .from('registrations')
          .select('id')
          .eq('user_id', user.id)
          .eq('camp_id', campIdParam)
          .not('payment_status', 'eq', 'Cancelled')
          .maybeSingle()

        if (existing) {
          navigate('/my-registration', { replace: true })
          return
        }

        // Fetch the specific camp
        const { data: campData } = await supabase
          .from('camps')
          .select('*')
          .eq('id', campIdParam)
          .maybeSingle()

        if (campData) {
          await loadCampData(campData)
          // openCamps is used for the "back" link — populate it too
          setOpenCamps([campData])
        }

        setPageLoading(false)
        return
      }

      // No campId param — show selector if multiple camps are open
      // Redirect if user already has ANY active registration.
      // Fix #5: .limit(1) prevents maybeSingle() from erroring when there
      // are multiple matching rows (e.g. user registered for two camps).
      const { data: existing } = await supabase
        .from('registrations')
        .select('id')
        .eq('user_id', user.id)
        .not('payment_status', 'eq', 'Cancelled')
        .limit(1)
        .maybeSingle()

      if (existing) {
        navigate('/my-registration', { replace: true })
        return
      }

      // Fetch all open camps
      const { data: campsData } = await supabase
        .from('camps')
        .select('*')
        .eq('registration_open', true)
        .order('start_date', { ascending: true })

      const camps = campsData ?? []
      setOpenCamps(camps)

      if (camps.length === 0) {
        setPageLoading(false)
        return
      }

      if (camps.length === 1) {
        await loadCampData(camps[0])
      } else {
        setStep('select')
      }

      setPageLoading(false)
    }
    load()
  }, [user.id, navigate, campIdParam])

  async function loadCampData(campData) {
    // Check for a previously cancelled registration for this specific camp
    const { data: cancelled } = await supabase
      .from('registrations')
      .select('id')
      .eq('user_id', user.id)
      .eq('camp_id', campData.id)
      .eq('payment_status', 'Cancelled')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    setCancelledRegId(cancelled?.id ?? null)
    setCamp(campData)

    const { data: tiersData } = await supabase
      .from('pricing_tiers')
      .select('*')
      .eq('camp_id', campData.id)
      .order('price', { ascending: true })
    setTiers(tiersData ?? [])
  }

  async function selectCamp(campData) {
    setSelectLoading(true)
    await loadCampData(campData)
    setStep('form')
    setSelectLoading(false)
  }

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!camp) return
    setError('')
    setSubmitting(true)

    // Pick the default tier (is_default = true), falling back to lowest price
    const defaultTier = tiers.find(tier => tier.is_default) ?? tiers[0] ?? null
    if (!defaultTier) {
      setError(t('campReg.noPricingTier'))
      setSubmitting(false)
      return
    }

    const transportCost = parseFloat(camp.transport_cost ?? 0)
    const amountOwed = defaultTier.price + (form.needs_transport ? transportCost : 0)

    const church = form.church === OTHER_CHURCH ? form.church_other.trim() : form.church

    const payload = {
      user_id: user.id,
      camp_id: camp.id,
      full_name: form.full_name,
      date_of_birth: form.date_of_birth,
      sex: form.sex,
      phone: form.phone,
      church,
      emergency_contact_name: form.emergency_contact_name,
      emergency_contact_phone: form.emergency_contact_phone,
      pricing_tier_id: defaultTier.id,
      arrival_date: defaultTier.arrival_date,
      needs_transport: form.needs_transport,
      amount_owed: amountOwed,
      payment_status: 'Pending',
    }

    let insertError
    let registrationId = cancelledRegId
    if (cancelledRegId) {
      // Reactivate the old cancelled row instead of creating a duplicate.
      // Reset amount_paid and refund_amount so the reactivated registration starts fresh.
      const { user_id: _uid, ...updatePayload } = payload
      const { error } = await supabase
        .from('registrations')
        .update({ ...updatePayload, amount_paid: 0, refund_amount: 0 })
        .eq('id', cancelledRegId)
      insertError = error
    } else {
      const { data, error } = await supabase
        .from('registrations')
        .insert(payload)
        .select('id')
        .single()
      insertError = error
      registrationId = data?.id ?? null
    }

    if (insertError) {
      setError(insertError.message)
      setSubmitting(false)
    } else {
      // Send the confirmation email with camp + payment details. Fire-and-forget:
      // a mail failure must never block the user from reaching their registration.
      if (registrationId) {
        supabase.functions
          .invoke('send-registration-confirmation', { body: { registration_id: registrationId } })
          .catch(err => console.error('[confirmation email]', err))
      }
      navigate('/my-registration')
    }
  }

  /* ── Loading ──────────────────────────────────────────────── */
  if (pageLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-8 h-8 border-4 border-oasis-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  /* ── Camp selector step ───────────────────────────────────── */
  if (step === 'select') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('campReg.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('campReg.selectSubtitle')}</p>
        </div>

        {selectLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-oasis-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {openCamps.map(c => (
              <button
                key={c.id}
                onClick={() => selectCamp(c)}
                className="w-full text-left bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:border-oasis-300 hover:shadow-md transition"
              >
                <div className="bg-oasis-500 px-6 py-2">
                  <span className="text-xs font-semibold uppercase tracking-widest text-oasis-100">
                    {t('campReg.openBadge')}
                  </span>
                </div>
                <div className="p-6 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-lg font-bold text-gray-900">{c.name}</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {c.location && <span>{c.location} · </span>}
                      {c.start_date && c.end_date && (
                        <span>{formatDate(c.start_date)} – {formatDate(c.end_date)}</span>
                      )}
                    </p>
                  </div>
                  <span className="flex-shrink-0 rounded-xl bg-oasis-500 px-5 py-2.5 text-sm font-semibold text-white">
                    {t('campReg.selectBtn')}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  /* ── No open camps ────────────────────────────────────────── */
  if (!camp) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center max-w-md">
          <span className="text-5xl">🏕️</span>
          <h2 className="mt-4 text-xl font-bold text-gray-900">{t('campReg.noOpenCampsTitle')}</h2>
          <p className="mt-2 text-sm text-gray-500">
            {t('campReg.noOpenCampsBody')}
          </p>
        </div>
      </div>
    )
  }

  /* ── Registration form ───────────────────────────────────── */
  return (
    <div className="max-w-2xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('campReg.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('campReg.registeringFor')} <span className="font-semibold text-oasis-700">{camp.name}</span>
          {camp.start_date && camp.end_date && (
            <> &mdash; {formatDate(camp.start_date)} to {formatDate(camp.end_date)}</>
          )}
        </p>
        {openCamps.length > 1 && (
          <button
            onClick={() => { setStep('select'); setCamp(null); setTiers([]) }}
            className="mt-2 text-xs text-oasis-500 hover:underline"
          >
            {t('campReg.chooseDifferentCamp')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <span className="mt-0.5 shrink-0">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">

        {/* ── Personal Information ────────────────────────────── */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5 pb-3 border-b border-gray-100">
            {t('campReg.personalInfo')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className={LABEL} htmlFor="full_name">{t('campReg.fullName')}</label>
              <input
                id="full_name"
                type="text"
                required
                maxLength={100}
                placeholder="Jane Smith"
                value={form.full_name}
                onChange={e => set('full_name', e.target.value)}
                className={INPUT}
              />
            </div>

            <div>
              <label className={LABEL} htmlFor="dob">{t('campReg.dateOfBirth')}</label>
              <input
                id="dob"
                type="date"
                required
                max={new Date().toISOString().split('T')[0]}
                value={form.date_of_birth}
                onChange={e => set('date_of_birth', e.target.value)}
                className={INPUT}
              />
            </div>

            <div>
              <label className={LABEL} htmlFor="sex">{t('campReg.sex')}</label>
              <select
                id="sex"
                required
                value={form.sex}
                onChange={e => set('sex', e.target.value)}
                className={INPUT}
              >
                <option value="">{t('campReg.sexSelect')}</option>
                <option value="Male">{t('campReg.sexMale')}</option>
                <option value="Female">{t('campReg.sexFemale')}</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className={LABEL} htmlFor="phone">{t('campReg.phoneNumber')}</label>
              <input
                id="phone"
                type="tel"
                required
                maxLength={20}
                placeholder="+1 (555) 000-0000"
                value={form.phone}
                onChange={e => set('phone', e.target.value)}
                className={INPUT}
              />
            </div>

            <div className="sm:col-span-2">
              <label className={LABEL} htmlFor="church">{t('campReg.church')}</label>
              <select
                id="church"
                required
                value={form.church}
                onChange={e => set('church', e.target.value)}
                className={INPUT}
              >
                <option value="">{t('campReg.churchSelect')}</option>
                {churches.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
                <option value={OTHER_CHURCH}>{t('campReg.churchOther')}</option>
              </select>
            </div>

            {form.church === OTHER_CHURCH && (
              <div className="sm:col-span-2">
                <label className={LABEL} htmlFor="church_other">{t('campReg.churchOtherLabel')}</label>
                <input
                  id="church_other"
                  type="text"
                  required
                  maxLength={100}
                  placeholder={t('campReg.churchOtherPlaceholder')}
                  value={form.church_other}
                  onChange={e => set('church_other', e.target.value)}
                  className={INPUT}
                />
              </div>
            )}
          </div>
        </section>

        {/* ── Emergency Contact ───────────────────────────────── */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5 pb-3 border-b border-gray-100">
            {t('campReg.emergencyContact')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={LABEL} htmlFor="ec_name">{t('campReg.contactName')}</label>
              <input
                id="ec_name"
                type="text"
                required
                maxLength={100}
                placeholder="John Smith"
                value={form.emergency_contact_name}
                onChange={e => set('emergency_contact_name', e.target.value)}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL} htmlFor="ec_phone">{t('campReg.contactPhone')}</label>
              <input
                id="ec_phone"
                type="tel"
                required
                maxLength={20}
                placeholder="+1 (555) 000-0000"
                value={form.emergency_contact_phone}
                onChange={e => set('emergency_contact_phone', e.target.value)}
                className={INPUT}
              />
            </div>
          </div>
        </section>

        {/* ── Camp Details ────────────────────────────────────── */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5 pb-3 border-b border-gray-100">
            {t('campReg.campDetails')}
          </h2>
          <div className="space-y-6">

            {/* Fee breakdown */}
            {(() => {
              const defaultTier = tiers.find(tier => tier.is_default) ?? tiers[0] ?? null
              const transportCost = parseFloat(camp.transport_cost ?? 0)
              const total = (defaultTier?.price ?? 0) + (form.needs_transport ? transportCost : 0)
              if (!defaultTier) return null
              return (
                <div className="rounded-xl bg-oasis-50 border border-oasis-100 px-4 py-4 space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{t('campReg.campFee')}</span>
                    <span className="font-medium">{formatCurrency(defaultTier.price)}</span>
                  </div>
                  {transportCost > 0 && (
                    <div className={`flex justify-between text-sm ${form.needs_transport ? 'text-gray-600' : 'text-gray-400'}`}>
                      <span>{t('campReg.transport')}</span>
                      <span className="font-medium">{form.needs_transport ? formatCurrency(transportCost) : t('campReg.transportIfNeeded', { amount: formatCurrency(transportCost) })}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-gray-900 border-t border-oasis-200 pt-2 mt-1 text-base">
                    <span>{t('campReg.total')}</span>
                    <span className="text-oasis-700">{formatCurrency(total)}</span>
                  </div>
                </div>
              )
            })()}

            {/* Transport toggle */}
            <div>
              <p className={LABEL}>{t('campReg.transportQuestion')}</p>
              <div className="flex gap-3 mt-1">
                {[false, true].map(val => (
                  <button
                    key={String(val)}
                    type="button"
                    onClick={() => set('needs_transport', val)}
                    className={`flex-1 sm:flex-none px-5 py-3 min-h-[44px] rounded-lg text-sm font-medium border transition ${
                      form.needs_transport === val
                        ? 'bg-oasis-500 text-white border-oasis-500 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {val ? t('campReg.yes') : t('campReg.no')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Submit ──────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto rounded-xl bg-oasis-500 px-8 py-3 text-sm font-semibold text-white hover:bg-oasis-700 focus:outline-none focus:ring-2 focus:ring-oasis-300 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
          >
            {submitting ? t('campReg.submitting') : t('campReg.completeRegistration')}
          </button>
        </div>

      </form>
    </div>
  )
}
